import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, History, RotateCw, Sparkles } from 'lucide-react'
import { api } from '@/lib/api'
import { StreamError, streamPost } from '@/lib/sse'
import { useStreamedText } from '@/lib/useStreamedText'
import type { AiConversation, AiMessage, AiMeta, AiProposal } from '@/types'
import Card from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Skeleton from '@/components/ui/Skeleton'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { useToast } from '@/components/ui/toast'
import ChatComposer from '@/components/admin/ai/ChatComposer'
import ChatMessage, { type ToolTrailItem } from '@/components/admin/ai/ChatMessage'
import ConversationList from '@/components/admin/ai/ConversationList'
import ProposalCard from '@/components/admin/ai/ProposalCard'

interface Bubble {
  id: number
  role: 'user' | 'assistant'
  content: string
  tools: ToolTrailItem[]
  /** Every stored message folded into this bubble, so a proposal finds the turn that made it. */
  ids: number[]
}

interface Turn {
  reasoning: string
  tools: ToolTrailItem[]
}

/**
 * Fold the stored transcript into bubbles. One answer is spread over an
 * assistant row that asked for tools, the tool rows, and the final row with
 * the words; only the last is a bubble, the rest become its "Used N sources".
 */
function toBubbles(messages: AiMessage[]): Bubble[] {
  const bubbles: Bubble[] = []
  let tools: ToolTrailItem[] = []
  let ids: number[] = []

  for (const message of messages) {
    if (message.role === 'user') {
      bubbles.push({ id: message.id, role: 'user', content: message.content ?? '', tools: [], ids: [message.id] })
      tools = []
      ids = []
      continue
    }

    if (message.role === 'tool') {
      ids.push(message.id)
      continue
    }

    // A turn that asked for tools is folded away even when it wrote a line
    // first: that line is filler on the way to the lookup.
    if (message.tool_calls?.length || !message.content) {
      tools = tools.concat((message.tool_calls ?? []).map((c) => ({ name: c.name ?? '', status: 'done' as const })))
      ids.push(message.id)
      continue
    }

    bubbles.push({ id: message.id, role: 'assistant', content: message.content, tools, ids: [...ids, message.id] })
    tools = []
    ids = []
  }

  return bubbles
}

/**
 * MPG AI, the admin planning assistant (Duncan 2026-10-26: help decide the
 * production day and each section's due date from the data). Admin only.
 * Dates come from the real planner through the API's tools; any change it
 * suggests is a card with Apply. Opening `?q=` fills the question box (the
 * order page's Ask MPG AI button) without sending it.
 */
export default function AdminAi() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [params, setParams] = useSearchParams()

  const [activeId, setActiveId] = useState<number | null>(null)
  const [draft, setDraft] = useState('')
  const [turn, setTurn] = useState<Turn | null>(null)
  const [pendingUser, setPendingUser] = useState<string | null>(null)
  const [error, setError] = useState<{ message: string; code?: string; text: string } | null>(null)
  const [deleting, setDeleting] = useState<AiConversation | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)

  const { shown: answer, push: pushAnswer, flush: flushAnswer, reset: resetAnswer } = useStreamedText()

  const abortRef = useRef<AbortController | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  // True while the reader is at the bottom; only their own scrolling changes it.
  const followRef = useRef(true)

  useEffect(() => {
    const q = params.get('q')
    if (q) {
      setDraft(q)
      setParams({}, { replace: true })
    }
  }, [params, setParams])

  const meta = useQuery({
    queryKey: ['ai', 'meta'],
    queryFn: () => api.get<{ data: AiMeta }>('/admin/ai/meta').then((r) => r.data.data),
  })

  const conversations = useQuery({
    queryKey: ['ai', 'conversations'],
    queryFn: () => api.get<{ data: AiConversation[] }>('/admin/ai/conversations').then((r) => r.data.data),
  })

  const active = useQuery({
    queryKey: ['ai', 'conversation', activeId],
    queryFn: () => api.get<{ data: AiConversation }>(`/admin/ai/conversations/${activeId}`).then((r) => r.data.data),
    enabled: activeId !== null,
  })

  const messages = active.data?.id === activeId ? (active.data?.messages ?? []) : []
  const proposals: AiProposal[] = active.data?.id === activeId ? (active.data?.proposals ?? []) : []
  const streaming = turn !== null
  const bubbles = toBubbles(messages)
  const isEmpty = bubbles.length === 0 && !pendingUser && !(activeId !== null && active.isLoading)

  const proposalsFor = (bubble: Bubble) => proposals.filter((p) => p.ai_message_id && bubble.ids.includes(p.ai_message_id))
  const anchored = new Set(bubbles.flatMap((b) => proposalsFor(b).map((p) => p.id)))
  const orphans = proposals.filter((p) => !anchored.has(p.id))

  const refreshConversation = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['ai', 'conversation', activeId] }),
    [queryClient, activeId],
  )

  const onScroll = () => {
    const el = scrollRef.current
    if (el) followRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }

  // Instant, never smooth: a smooth scroll fires scroll events mid-flight,
  // which would read as the reader scrolling away and stop the following.
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (el && followRef.current) el.scrollTop = el.scrollHeight
  }, [messages.length, proposals.length, pendingUser, answer, turn?.reasoning, turn?.tools.length, error])

  const createConversation = useMutation({
    mutationFn: () => api.post<{ data: AiConversation }>('/admin/ai/conversations').then((r) => r.data.data),
    onSuccess: (conversation) => {
      // Seed the empty transcript so the panel does not flash a loader.
      queryClient.setQueryData(['ai', 'conversation', conversation.id], { ...conversation, messages: [], proposals: [] })
      queryClient.invalidateQueries({ queryKey: ['ai', 'conversations'] })
      setActiveId(conversation.id)
    },
  })

  const removeConversation = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/ai/conversations/${id}`),
    onSuccess: (_res, id) => {
      if (id === activeId) setActiveId(null)
      setDeleting(null)
      toast.success('Chat deleted')
      queryClient.invalidateQueries({ queryKey: ['ai', 'conversations'] })
    },
    onError: () => toast.error('Could not delete that chat'),
  })

  const send = useCallback(
    async (raw: string) => {
      const text = raw.trim()
      if (!text || streaming) return

      setError(null)
      setDraft('')
      followRef.current = true
      resetAnswer()
      // Busy before the first await, so a second Enter cannot start a parallel turn.
      setPendingUser(text)
      setTurn({ reasoning: '', tools: [] })

      let conversationId = activeId
      try {
        conversationId ??= (await createConversation.mutateAsync()).id
      } catch {
        setError({ message: 'Could not start a new chat. Try again.', text })
        setPendingUser(null)
        setTurn(null)
        return
      }

      const controller = new AbortController()
      abortRef.current = controller
      // A stream the server dropped (a PHP time limit, a proxy) ends with
      // neither `done` nor `error`; without this the question just sits there.
      let finished = false

      try {
        await streamPost(
          `/admin/ai/conversations/${conversationId}/messages`,
          { message: text },
          {
            signal: controller.signal,
            onEvent: (event, data) => {
              if (event === 'delta') {
                pushAnswer(String(data.text ?? ''))
              } else if (event === 'reason') {
                setTurn((t) => (t ? { ...t, reasoning: t.reasoning + String(data.text ?? '') } : t))
              } else if (event === 'tool') {
                // Text before a round of lookups is filler; the stored
                // transcript folds it away, so drop it live too.
                if (data.status === 'running') resetAnswer()
                setTurn((t) => {
                  if (!t) return t
                  const tools = [...t.tools]
                  const i = tools.findIndex((x) => x.name === data.name && x.status === 'running')
                  if (data.status === 'done' && i !== -1) tools[i] = { ...tools[i], status: 'done' }
                  else tools.push({ name: String(data.name ?? ''), status: data.status === 'done' ? 'done' : 'running' })
                  return { ...t, tools }
                })
              } else if (event === 'error') {
                finished = true
                setError({ message: String(data.message ?? 'MPG AI hit an error.'), text })
              } else if (event === 'done') {
                finished = true
              }
            },
          },
        )
        if (!finished && !controller.signal.aborted) {
          setError({ message: 'The answer was cut off before it finished. Try again.', text })
        }
      } catch (e) {
        // Stop is the button working, not a failure.
        if (!(e instanceof DOMException && e.name === 'AbortError')) {
          setError({
            message: e instanceof Error ? e.message : 'MPG AI could not answer.',
            code: e instanceof StreamError ? e.code : undefined,
            text,
          })
        }
      } finally {
        // Reveal what is buffered BEFORE swapping in the stored answer.
        await flushAnswer()
        await queryClient.invalidateQueries({ queryKey: ['ai', 'conversation', conversationId] })
        queryClient.invalidateQueries({ queryKey: ['ai', 'conversations'] })
        queryClient.invalidateQueries({ queryKey: ['ai', 'meta'] })
        abortRef.current = null
        setTurn(null)
        setPendingUser(null)
      }
    },
    [activeId, streaming, createConversation, queryClient, pushAnswer, flushAnswer, resetAnswer],
  )

  const openChat = (id: number | null) => {
    setActiveId(id)
    setError(null)
    setHistoryOpen(false)
    followRef.current = true
  }

  const list = (
    <ConversationList
      conversations={conversations.data ?? []}
      loading={conversations.isLoading}
      activeId={activeId}
      onSelect={openChat}
      onCreate={() => openChat(null)}
      onDelete={setDeleting}
      usage={meta.data?.usage}
    />
  )

  return (
    <div>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <span className="ai-ring inline-flex size-8">
              <span className="flex size-full items-center justify-center rounded-full bg-surface text-primary">
                <Sparkles className="size-4" />
              </span>
            </span>
            MPG AI
          </span>
        }
        subtitle="Ask about production days, section due dates and late jobs. Dates come from the real planner; changes wait for you to press Apply."
        actions={
          <button
            type="button"
            onClick={() => setHistoryOpen((open) => !open)}
            className="inline-flex h-10 items-center gap-2 rounded-lg border border-foreground/20 bg-background px-4 text-sm font-medium hover:bg-foreground/5 lg:hidden"
          >
            <History className="size-4" /> Chats
          </button>
        }
      />

      {historyOpen && <Card className="mb-4 max-h-80 p-3 lg:hidden">{list}</Card>}

      <div className="grid gap-4 lg:grid-cols-[240px_minmax(0,1fr)]">
        <Card className="hidden h-[calc(100dvh-13rem)] min-h-[28rem] p-3 lg:block">{list}</Card>

        <Card className="flex h-[calc(100dvh-13rem)] min-h-[28rem] min-w-0 flex-col p-3 sm:p-4">
          <div ref={scrollRef} onScroll={onScroll} className="-mr-2 min-h-0 flex-1 space-y-5 overflow-y-auto pr-2">
            {activeId !== null && active.isLoading && !pendingUser && (
              <div className="space-y-3">
                <Skeleton className="ml-auto h-10 w-2/3" />
                <Skeleton className="h-24" />
              </div>
            )}

            {isEmpty && (
              <div className="flex flex-col items-center px-2 py-6 text-center sm:py-10">
                <span className="ai-ring inline-flex size-12">
                  <span className="flex size-full items-center justify-center rounded-full bg-surface text-primary">
                    <Sparkles className="size-6" />
                  </span>
                </span>
                <p className="mt-3 font-medium text-foreground">What do you want to plan?</p>
                <p className="mt-1 max-w-md text-sm text-foreground/60">
                  MPG AI reads your orders, section load and finished jobs. It runs the same planner that books jobs, so every date it gives is one the system would book.
                </p>
                <div className="mt-5 grid w-full max-w-2xl gap-2 sm:grid-cols-2">
                  {(meta.data?.suggestions ?? []).map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      onClick={() => send(prompt)}
                      className="rounded-xl border border-foreground/10 bg-background px-3 py-2.5 text-left text-sm text-foreground/80 hover:border-primary/40 hover:bg-primary/5"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {bubbles.map((bubble) => (
              <div key={bubble.id}>
                <ChatMessage role={bubble.role} content={bubble.content} tools={bubble.tools} />
                {proposalsFor(bubble).map((proposal) => (
                  <div key={proposal.id} className="sm:pl-10">
                    <ProposalCard proposal={proposal} onChanged={refreshConversation} />
                  </div>
                ))}
              </div>
            ))}

            {!streaming &&
              orphans.map((proposal) => (
                <div key={proposal.id} className="sm:pl-10">
                  <ProposalCard proposal={proposal} onChanged={refreshConversation} />
                </div>
              ))}

            {pendingUser && <ChatMessage role="user" content={pendingUser} />}

            {turn && <ChatMessage role="assistant" streaming content={answer} reasoning={turn.reasoning} tools={turn.tools} />}

            {error && (
              <div className="flex items-start gap-2 rounded-xl border border-danger/40 bg-danger/5 p-3">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-danger" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-foreground">{error.message}</p>
                  {error.code !== 'monthly_cap' && error.code !== 'ai_disabled' && (
                    <button
                      type="button"
                      onClick={() => send(error.text)}
                      className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                    >
                      <RotateCw className="size-3" /> Try again
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          <ChatComposer value={draft} onChange={setDraft} onSend={() => send(draft)} onStop={() => abortRef.current?.abort()} busy={streaming} />
        </Card>
      </div>

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && removeConversation.mutate(deleting.id)}
        title="Delete this chat?"
        confirmLabel="Delete"
        variant="danger"
        loading={removeConversation.isPending}
      >
        "{deleting?.title}" and everything in it will be removed for good. Changes already applied stay applied.
      </ConfirmDialog>
    </div>
  )
}
